import React, { Component } from 'react';
import PropTypes from 'prop-types'
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { actions as userActions } from '../../actions/userActions';
import * as types from '../../constants/actionTypes';
import Drawer from '../Common/Drawer/Drawer';
import UserLocationsForm from './UserLocationsForm';
import LoadingIndicator from '../Common/LoadingIndicator/LoadingIndicator';
import Notification from '../Common/Notification';

export class UserLocationsDrawer extends Component {

    constructor(props, context) {
        super(props, context);

        this.state = {
            isLoading: false,
            isFormValid: true,
            filters: {
                assignedLocations: [],
                availableForAssignmentLocations: [],
                profileRuleGrantedLocations: [],
                defaultLocationId: '',
                isUserGrantedAccessToAllLocations: null,  //false = no, true = yes, null = based on rule
                availableForDefaultLocations: []
            }
        };
    }

    async componentDidMount() {
        this.loadUserLocations(this.props.user.id, this.props.subscriptionId);
    }

    onCancel = () => {
        this.props.onComplete();
    }

    loadUserLocations = async (userId, subId) => {
        try {
            this.setState({ isLoading: true });
            const response = await this.props.userActions.loadLocationsFilters(userId, subId);
            if (response.type === types.LOAD_LOCATIONS_FILTERS_SUCCESS) {

                let availableForDefaultLocations = response.payload.assignedLocations.concat(response.payload.profileRuleGrantedLocations);
                // if they are granted access to all locations, put the defaultLocation (if one exists) in the availableForDefaultLocation array if it's not already in there
                // isUserGrantedAccessToAllLocations could be undefined if it is null (yes, based on rule) in the database!
                if(!response.payload.isUserGrantedAccessToAllLocations && response.payload.defaultLocation){
                    const foundDefaultLocation = availableForDefaultLocations.find(m => m.id === response.payload.defaultLocation.id);
                    if(foundDefaultLocation === undefined){
                        availableForDefaultLocations.push(response.payload.defaultLocation);
                    }
                }
                const obj = {
                    assignedLocations: response.payload.assignedLocations,
                    availableForAssignmentLocations: response.payload.availableForAssignmentLocations,
                    profileRuleGrantedLocations: response.payload.profileRuleGrantedLocations,
                    defaultLocationId: (!response.payload.defaultLocation || response.payload.defaultLocation.id === 0) ? "" : response.payload.defaultLocation.id,
                    isUserGrantedAccessToAllLocations: (response.payload.isUserGrantedAccessToAllLocations != undefined) ? response.payload.isUserGrantedAccessToAllLocations : null,
                    availableForDefaultLocations: availableForDefaultLocations
                }

                const newFilters = { ...this.state.filters, ...obj }
                this.setState({ filters: newFilters });

            } else if (response.type === types.LOAD_LOCATIONS_FILTERS_FAILURE) {
                Notification("error", "An Error Occurred", response.payload.message);
            }
        } catch (error) {
            Notification("error", "Error", error);
        } finally {
            this.setState({ isLoading: false });
        }
    }

    launchBulkLocations = () => {
        this.props.onBulkLocationSearchLaunch(this.addBulkLocations, this.state.filters.assignedLocations);
    }

    addBulkLocations = (locations) => {
        const assignedLocations = [
            ...this.state.filters.assignedLocations,
            ...locations
        ];

        let defaultLocationId = this.state.filters.defaultLocationId;
        if (!defaultLocationId && assignedLocations.length > 0) {
            defaultLocationId = assignedLocations[0].id;
        }

        // Remove the assigned locations from the list of locations available for assignment
        let availableForAssignmentLocations = this.state.filters.availableForAssignmentLocations;
        locations.forEach(location => {
            const availableAssignmentIndex = availableForAssignmentLocations.findIndex(x => x.id === location.id);
            if (availableAssignmentIndex >= 0) {
                availableForAssignmentLocations.splice(availableAssignmentIndex, 1);
            }
        })

        // Ensure the locations available for default include all assigned locations as well
        // as all locations granted access via the user's profile
        let availableForDefaultLocations = assignedLocations.concat(this.state.filters.profileRuleGrantedLocations);

        [ availableForDefaultLocations, availableForAssignmentLocations ] = this.reSortAvailableLists(availableForDefaultLocations, availableForAssignmentLocations);

        this.setState({
            filters: {
                ...this.state.filters,
                assignedLocations: assignedLocations,
                defaultLocationId: defaultLocationId,
                availableForDefaultLocations: availableForDefaultLocations,
                availableForAssignmentLocations: availableForAssignmentLocations
            }
        })
    }

    /**
     * Re-sort the available for default and available for assignment lists, because people
     * have to choose from there in a drop-down format.  Do not resort the assignment list
     * because that's the one really being edited, so it is easier to just see the ones
     * recently edited at the end of the list.
     */
    reSortAvailableLists(availableForDefaultLocations, availableForAssignmentLocations) {
        availableForDefaultLocations.sort((a, b) => a.name < b.name ? -1 : a.name === b.name ? 0 : 1);
        availableForAssignmentLocations.sort((a, b) => a.name < b.name ? -1 : a.name === b.name ? 0 : 1);

        return [ availableForDefaultLocations, availableForAssignmentLocations ];
    }

    addLocation = (option) => {
        // Add the location to the list of assigned locations
        let assignedLocations = this.state.filters.assignedLocations.slice();
        const assignedIndex = assignedLocations.findIndex(x => x.id === option.id);
        if (assignedIndex < 0) {
            assignedLocations.push(option);
        }

        // Add the location to the list of locations available to be the default location,
        // UNLESS that location is already available because its access is granted via
        // the user's profile settings.
        let availableForDefaultLocations = this.state.filters.availableForDefaultLocations;
        const profileIndex = this.state.filters.profileRuleGrantedLocations.findIndex(x => x.id === option.id);
        const availableIndex = availableForDefaultLocations.findIndex(x => x.id === option.id);
        if (availableIndex < 0 && profileIndex < 0) {
            availableForDefaultLocations.push(option);
        }

        // Remove the assigned location from the list of locations available for assignment
        let availableForAssignmentLocations = this.state.filters.availableForAssignmentLocations;
        const availableAssignmentIndex = availableForAssignmentLocations.findIndex(x => x.id === option.id);
        if (availableAssignmentIndex >= 0) {
            availableForAssignmentLocations.splice(availableAssignmentIndex, 1);
        }

        // Automatically set the first location as the default location there is only
        // one location available.
        let defaultLocationId = this.state.filters.defaultLocationId;
        if (availableForDefaultLocations.length === 1) {
            defaultLocationId = availableForDefaultLocations[0].id.toString();
        }

        [ availableForDefaultLocations, availableForAssignmentLocations ] = this.reSortAvailableLists(availableForDefaultLocations, availableForAssignmentLocations);

        const tempFilters = {
            assignedLocations: assignedLocations,
            defaultLocationId: defaultLocationId,
            availableForDefaultLocations: availableForDefaultLocations,
            availableForAssignmentLocations: availableForAssignmentLocations
        }

        const newFilters = { ... this.state.filters, ...tempFilters };
        this.setState({ filters: newFilters });

    }

    /**
     * RemoveLocation operates on potentially multiple selected items, so we need
     * to grab all the selected items from 
     */
    removeLocation = (locations) => {
        let assignedLocations = this.state.filters.assignedLocations.slice();
        let availableForDefaultLocations = this.state.filters.availableForDefaultLocations;
        let availableForAssignmentLocations = this.state.filters.availableForAssignmentLocations;

        locations.forEach(location => {
            // Remove location from the list of assigned locations
            const assignedIndex = assignedLocations.findIndex(x => x.id === location.id);
            if (assignedIndex >= 0) {
                assignedLocations.splice(assignedIndex, 1);
            }

            // Remove location from the list of available default locations, UNLESS that user also has
            // access to this location via their profile settings.
            const profileIndex = this.state.filters.profileRuleGrantedLocations.findIndex(x => x.id === location.id);
            const availableIndex = availableForDefaultLocations.findIndex(x => x.id === location.id);
            if (availableIndex >= 0 && profileIndex < 0) {
                availableForDefaultLocations.splice(availableIndex, 1);
            }

            // Add removed location to the list of locations available for assignment
            const availableAssignmentIndex = availableForAssignmentLocations.findIndex(x => x.id === location.id);
            if (availableAssignmentIndex < 0) {
                availableForAssignmentLocations.push(location);
            }
        })

        // When there are no locations available, or the currently set location has been 
        // removed from the list of available locations, unset the default location Id value
        let defaultLocationId = this.state.filters.defaultLocationId;
        const defaultIndex = availableForDefaultLocations.findIndex(x => x.id === defaultLocationId);
        if (availableForDefaultLocations.length === 0 || defaultIndex < 0) {
            defaultLocationId = "";
        }

        [ availableForDefaultLocations, availableForAssignmentLocations ] = this.reSortAvailableLists(availableForDefaultLocations, availableForAssignmentLocations);

        const tempFilters = {
            assignedLocations: assignedLocations,
            defaultLocationId: defaultLocationId,
            availableForDefaultLocations: availableForDefaultLocations,
            availableForAssignmentLocations: availableForAssignmentLocations
        }

        const newFilters = { ... this.state.filters, ...tempFilters };
        this.setState({ filters: newFilters });

    }

    defaultLocationChange = (newDefaultLocationId) => {
        this.setState({ filters: { ...this.state.filters, defaultLocationId: newDefaultLocationId.toString() } });
    }

    validateForm = () => {

        const assignedLocationCount = this.state.filters.assignedLocations.length;
        const grantedLocationCount = this.state.filters.profileRuleGrantedLocations.length;

        if (assignedLocationCount > 0 && (!this.state.filters.defaultLocationId || this.state.filters.defaultLocationId === "")) {
            return false;
        } else if (grantedLocationCount > 0 && (!this.state.filters.defaultLocationId || this.state.filters.defaultLocationId === "")) {
            return false;
        } else {
            return true;
        }
    }

    onSave = async () => {
        try {
            if (this.validateForm()) {
                this.setState({ isFormValid: true, isLoading: true });

                const assignedLocationIds = [];

                for (let i = 0; i < this.state.filters.assignedLocations.length; i++) {
                    assignedLocationIds.push(this.state.filters.assignedLocations[i].id);
                }

                const defaultLocationId = (!this.state.filters.defaultLocationId || this.state.filters.defaultLocationId === "") ? null : this.state.filters.defaultLocationId;

                const request = {
                    userId: this.props.user.id,
                    subscriptionId: this.props.subscriptionId,
                    defaultLocationId: defaultLocationId,
                    locationIds: assignedLocationIds
                }

                const result = await this.props.userActions.updateLocations(request);

                if (result.type === types.UPDATE_USER_LOCATIONS_SUCCESS) {
                    Notification("success", "Success", "Locations saved successfully");
                    this.setState({ isFormValid: true, isLoading: false });
                    this.props.onComplete();
                } else if (result.type === types.UPDATE_USER_LOCATIONS_FAILURE) {
                    Notification("error", "Error", result.payload.message);
                    this.setState({ isLoading: false });
                }
            } else {
                this.setState({ isFormValid: false, isLoading: false });
            }
        } catch (error) {
            Notification("error", "Error", error);
            this.setState({ isLoading: false });
        } finally {
            // Do not set isLoading state here. If the save was successful, the 
            // component is unloaded already because of the redirect
        }
    }

    render() {
        return (
            <Drawer isWide={false} title="User Location Access"
                onSaveClick={this.onSave}
                saveText='Save'
                onCancelClick={this.onCancel}
                menuButtons={this.props.menuButtons}
            >
                <LoadingIndicator isLoading={this.state.isLoading}>
                    <UserLocationsForm
                        user={this.props.user}
                        assignedLocations={this.state.filters.assignedLocations}
                        availableForAssignmentLocations={this.state.filters.availableForAssignmentLocations}
                        availableForDefaultLocations={this.state.filters.availableForDefaultLocations}
                        profileRuleGrantedLocations={this.state.filters.profileRuleGrantedLocations}
                        defaultLocationId={this.state.filters.defaultLocationId.toString()}
                        isUserGrantedAccessToAllLocations={this.state.filters.isUserGrantedAccessToAllLocations}
                        addLocation={this.addLocation}
                        removeLocation={this.removeLocation}
                        onDefaultLocationChange={this.defaultLocationChange}
                        isFormValid={this.state.isFormValid}
                        onBulkLocationSearchLaunch={this.launchBulkLocations}
                    />
                </LoadingIndicator>
            </Drawer>
        )
    }
}

UserLocationsDrawer.propTypes = {
    userActions: PropTypes.object.isRequired,
    subscriptionId: PropTypes.string.isRequired,
    onComplete: PropTypes.func.isRequired,
    user: PropTypes.object.isRequired,
    menuButtons: PropTypes.array.isRequired,
    onBulkLocationSearchLaunch: PropTypes.func.isRequired
};

function mapDispatchToProps(dispatch) {
    return {
        userActions: bindActionCreators(userActions, dispatch),
    };
}

export default connect(null, mapDispatchToProps)(UserLocationsDrawer);
